import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Grid3X3, LayoutList, Sparkles, TrendingUp, Clock, Heart } from 'lucide-react';
import { getComics } from '../services/api';
import { ComicCard, ComicCardSkeleton } from '../components/ComicCard';
import { AdBanner } from '../components/AdBanner';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';

// Genre configuration
const GENRES = [
  { value: 'action', label: 'Action', color: 'from-red-500 to-orange-500' },
  { value: 'romance', label: 'Romance', color: 'from-pink-500 to-rose-500' },
  { value: 'comedy', label: 'Comedy', color: 'from-yellow-500 to-amber-500' },
  { value: 'fantasy', label: 'Fantasy', color: 'from-purple-500 to-violet-500' },
  { value: 'horror', label: 'Horror', color: 'from-gray-600 to-gray-800' },
  { value: 'sci-fi', label: 'Sci-Fi', color: 'from-cyan-500 to-blue-500' },
  { value: 'slice_of_life', label: 'Slice of Life', color: 'from-green-500 to-emerald-500' },
  { value: 'superhero', label: 'Superhero', color: 'from-blue-500 to-indigo-500' },
  { value: 'thriller', label: 'Thriller', color: 'from-emerald-500 to-teal-500' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Newest', icon: Clock },
  { value: 'views', label: 'Popular', icon: TrendingUp },
  { value: 'likes', label: 'Most Loved', icon: Heart },
];

const BrowsePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_at');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchComics = async () => {
      setLoading(true);
      try {
        const params = {
          limit: 50,
          sort_by: sortBy
        };
        if (searchTerm) params.search = searchTerm;
        if (selectedGenre) params.genre = selectedGenre;
        
        const res = await getComics(params);
        setComics(res.data);
      } catch (error) {
        console.error('Error fetching comics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchComics();
  }, [searchTerm, selectedGenre, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedGenre) params.set('genre', selectedGenre);
    if (sortBy !== 'created_at') params.set('sort', sortBy);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenre('');
    setSortBy('created_at');
    setSearchParams({});
  };

  const hasActiveFilters = searchTerm || selectedGenre || sortBy !== 'created_at';

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="browse-page">
      <SEO title="Browse Comics" description="Discover amazing comics across all genres on Supremics" />
      
      {/* Hero Header */}
      <div className="relative bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--primary)]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[var(--secondary)]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative page-padding pt-8 pb-12">
          <div className="max-w-7xl mx-auto">
            {/* Title Section */}
            <div className="text-center mb-8">
              <p className="text-xs tracking-[0.3em] text-[var(--primary)] uppercase mb-2">Explore</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-teko font-semibold mb-3">
                Discover Comics
              </h1>
              <p className="text-[var(--text-secondary)] max-w-md mx-auto">
                Find your next favorite story from our collection of {comics.length}+ comics
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative group">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] group-focus-within:text-[var(--primary)] transition-colors" size={20} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by title, genre, or creator..."
                  className="w-full pl-14 pr-32 py-4 bg-[var(--surface)] border-[var(--border)] rounded-full text-base focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 transition-all"
                  data-testid="search-input"
                />
                <button 
                  type="submit" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-full text-sm font-medium transition-colors"
                  data-testid="search-btn"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-padding py-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Filters Bar */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            {/* Left: Genre Pills */}
            <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2 lg:pb-0">
              <button
                onClick={() => setSelectedGenre('')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedGenre 
                    ? 'bg-[var(--primary)] text-white' 
                    : 'bg-[var(--surface)] text-[var(--text-secondary)] hover:bg-[var(--surface-highlight)] border border-[var(--border)]'
                }`}
              >
                All
              </button>
              {GENRES.map((genre) => (
                <button
                  key={genre.value}
                  onClick={() => setSelectedGenre(genre.value === selectedGenre ? '' : genre.value)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedGenre === genre.value 
                      ? `bg-gradient-to-r ${genre.color} text-white` 
                      : 'bg-[var(--surface)] text-[var(--text-secondary)] hover:bg-[var(--surface-highlight)] border border-[var(--border)]'
                  }`}
                  data-testid={`genre-${genre.value}`}
                >
                  {genre.label}
                </button>
              ))}
            </div>

            {/* Right: Sort & View Controls */}
            <div className="flex items-center gap-3">
              {/* Sort Options */}
              <div className="flex items-center bg-[var(--surface)] border border-[var(--border)] rounded-lg p-1">
                {SORT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                        sortBy === option.value 
                          ? 'bg-[var(--primary)]/10 text-[var(--primary)]' 
                          : 'text-[var(--text-secondary)] hover:text-[var(--foreground)]'
                      }`}
                      data-testid={`sort-${option.value}`}
                    >
                      <Icon size={14} />
                      <span className="hidden sm:inline">{option.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* View Toggle */}
              <div className="flex items-center bg-[var(--surface)] border border-[var(--border)] rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-[var(--primary)]/10 text-[var(--primary)]' 
                      : 'text-[var(--text-secondary)] hover:text-[var(--foreground)]'
                  }`}
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'list' 
                      ? 'bg-[var(--primary)]/10 text-[var(--primary)]' 
                      : 'text-[var(--text-secondary)] hover:text-[var(--foreground)]'
                  }`}
                >
                  <LayoutList size={18} />
                </button>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-[var(--text-secondary)] text-sm">
              {loading ? 'Loading...' : `${comics.length} comics found`}
              {selectedGenre && <span className="ml-1">in <span className="text-[var(--primary)] capitalize">{selectedGenre.replace('_', ' ')}</span></span>}
            </p>
          </div>

          {/* Comics Grid */}
          {loading ? (
            <div className={`grid gap-4 ${
              viewMode === 'grid' 
                ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' 
                : 'grid-cols-1 md:grid-cols-2'
            }`}>
              {[...Array(12)].map((_, i) => (
                <ComicCardSkeleton key={`skeleton-${i}`} compact={viewMode === 'grid'} />
              ))}
            </div>
          ) : comics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[var(--surface)] flex items-center justify-center">
                <Search size={32} className="text-[var(--text-secondary)]" />
              </div>
              <h3 className="text-xl font-teko mb-2">No comics found</h3>
              <p className="text-[var(--text-secondary)] mb-6">Try adjusting your search or filters</p>
              <button onClick={clearFilters} className="btn-hotrod">
                <span>Clear Filters</span>
              </button>
            </div>
          ) : (
            <div className={`grid gap-4 ${
              viewMode === 'grid' 
                ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6' 
                : 'grid-cols-1 md:grid-cols-2'
            }`}>
              {comics.map((comic, index) => (
                <React.Fragment key={comic.id}>
                  {viewMode === 'grid' ? (
                    <ComicCard comic={comic} compact />
                  ) : (
                    <ListViewCard comic={comic} />
                  )}
                  {/* Ad after every 18 comics in grid, 6 in list */}
                  {(index + 1) % (viewMode === 'grid' ? 18 : 6) === 0 && index < comics.length - 1 && (
                    <div className={viewMode === 'grid' ? 'col-span-full' : 'col-span-full md:col-span-2'}>
                      <AdBanner size="leaderboard" variant="subtle" className="rounded-xl" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// List View Card Component
function ListViewCard({ comic }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group flex gap-4 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)]/50 transition-all"
      data-testid={`comic-list-${comic.id}`}
    >
      <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden">
        <img 
          src={comic.cover_image} 
          alt={comic.title}
          className="w-full h-full object-cover transition-transform group-hover:scale-105"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-xs text-[var(--primary)] uppercase tracking-wider">
              {comic.genre?.replace('_', ' ')}
            </span>
            <h3 className="font-teko text-xl mt-1 group-hover:text-[var(--primary)] transition-colors line-clamp-1">
              {comic.title}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              by {comic.creator_name || comic.artist_name}
            </p>
          </div>
          {!comic.is_free && (
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 text-xs rounded-full">
              Premium
            </span>
          )}
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-2 line-clamp-2">
          {comic.description}
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs text-[var(--text-secondary)]">
          <span className="flex items-center gap-1">
            <TrendingUp size={12} />
            {comic.views?.toLocaleString() || 0} views
          </span>
          <span className="flex items-center gap-1">
            <Heart size={12} />
            {comic.likes?.toLocaleString() || 0} likes
          </span>
          <span>{comic.episode_count || 0} episodes</span>
        </div>
      </div>
    </Link>
  );
}

export default BrowsePage;
